// src/types/treatmentRoom.ts

// ──────────────────────────────────────────────────────────────────────────────
// 치료실 카테고리

export interface TreatmentRoomSerializer {
  treatmentRoomUuid?: string // 치료실 UUID
  treatmentRoomBedNumber?: string // 치료실 베드 번호
  treatmentRoomPatientName?: string // 환자 이름
  treatmentRoomPatientUuid?: string // 환자 UUID
  treatmentRoomPatientId?: string // 환자 차트번호
  treatmentRoomPatientGender?: string // 환자 성별
  treatmentRoomPatientAge?: string // 환자 나이
  treatmentRoomInsuranceType?: string // 보험 구분
  treatmentRoomInsuranceNhisType?: string // 건보 세부 구분
  treatmentRoomDoctorUuid?: string // 진료의 UUID
  treatmentRoomDoctorName?: string // 진료의 이름
  treatmentRoomStartedAt?: string // 치료 시작 시간
  treatmentRoomStatus?: string // 치료 상태
  treatmentRoomItems?: TreatmentRoomItemSerializer[] // 치료 항목 시작 시간
}

// ──────────────────────────────────────────────────────────────────────────────
// 치료 항목 관련 타입

/** 기본 치료 항목 정의 */
export interface TreatmentItemDef {
  id: string // 고유 ID
  name: string // 전체 이름 (ex: 핫팩, ICT, 부항)
  shortName: string // 축약 이름 (ex: 핫, I, 부) - 첫글자
  order: number // 표시 순서
  enabled: boolean // 활성화 여부
  timerMinutes?: number // 타이머 기본 설정(분)
}

/** 환자별 치료 항목 진행 상태 */
export type TreatmentItemState = "none" | "active" | "done" // none: 미선택, active: 진행중(빨강), done: 완료(파랑)

// 추나 배지 완료 상태를 기존 치료 항목 배열에 숨김 메타로 함께 저장할 때 사용하는 전용 ID
export const CHUNA_BADGE_STATUS_ITEM_ID = "__chuna_badge_status__"

export interface TreatmentItemStatus {
  itemId: string
  name?: string
  state: TreatmentItemState
  startedAt?: string
  timerMinutes?: number
}

/** 치료실 API 응답용 아이템 (서버 serializer 대응) */
export interface TreatmentRoomItemSerializer {
  itemId?: string
  state?: TreatmentItemState
  startedAt?: string
  timerMinutes?: number
  name?: string // legacy/optional
}

export interface TreatmentRoomThemeSettings {
  backgroundPresetId: string
  pageBackgroundColor: string
  headerBackgroundColor: string
  headerTitleColor: string
  headerEyebrowColor: string
  headerDescriptionColor: string
  iconButtonBackgroundColor: string
  iconButtonBorderColor: string
  iconButtonTextColor: string
  cardBackgroundColor: string
  emptyCardBackgroundColor: string
  cardBorderColor: string
  cardHeaderBackgroundColor: string
  cardHeaderTextColor: string
  cardAccentColor: string
  emptyCardAccentColor: string
  primaryTextColor: string
  secondaryTextColor: string
  accentTextColor: string
  doctorBadgeBackgroundColor: string
  doctorBadgeTextColor: string
  patientBadgeBackgroundColor: string
  patientBadgeTextColor: string
  endButtonBackgroundColor: string
  endButtonTextColor: string
  adjustPositiveButtonBackgroundColor: string
  adjustPositiveButtonTextColor: string
  adjustNegativeButtonBackgroundColor: string
  adjustNegativeButtonTextColor: string
  treatmentBeforeBackgroundColor: string
  treatmentBeforeBorderColor: string
  treatmentBeforeTextColor: string
  treatmentActiveBackgroundColor: string
  treatmentActiveBorderColor: string
  treatmentActiveTextColor: string
  treatmentDoneBackgroundColor: string
  treatmentDoneBorderColor: string
  treatmentDoneTextColor: string
  untreatedZoneBackgroundColor: string
  untreatedZoneBorderColor: string
  untreatedChipBackgroundColor: string
  untreatedChipBorderColor: string
  untreatedChipTextColor: string
}

export interface TreatmentRoomThemePreset {
  id: string
  name: string
  description: string
  colors: Partial<TreatmentRoomThemeSettings>
}

export const DEFAULT_TREATMENT_TIMER_MINUTES = 10
export const HIDDEN_TREATMENT_STATUS_NAME_PREFIX = "__hidden__::"

export const DEFAULT_TREATMENT_ROOM_THEME: TreatmentRoomThemeSettings = {
  backgroundPresetId: "sky",
  pageBackgroundColor: "#e7f0f8",
  headerBackgroundColor: "#f6fbff",
  headerTitleColor: "#16222d",
  headerEyebrowColor: "#0196da",
  headerDescriptionColor: "#507291",
  iconButtonBackgroundColor: "#ffffff",
  iconButtonBorderColor: "#d3dde6",
  iconButtonTextColor: "#5f6d7a",
  cardBackgroundColor: "#f8fbfe",
  emptyCardBackgroundColor: "#eef5fb",
  cardBorderColor: "#cfe0ec",
  cardHeaderBackgroundColor: "#edf5fb",
  cardHeaderTextColor: "#1c2b39",
  cardAccentColor: "#1e9bdc",
  emptyCardAccentColor: "#95b8d3",
  primaryTextColor: "#16222d",
  secondaryTextColor: "#6a7d8e",
  accentTextColor: "#1696dd",
  doctorBadgeBackgroundColor: "#2f8d36",
  doctorBadgeTextColor: "#ffffff",
  patientBadgeBackgroundColor: "#edf1f5",
  patientBadgeTextColor: "#22313f",
  endButtonBackgroundColor: "#6d6d6d",
  endButtonTextColor: "#ffffff",
  adjustPositiveButtonBackgroundColor: "#1696dd",
  adjustPositiveButtonTextColor: "#ffffff",
  adjustNegativeButtonBackgroundColor: "#6d6d6d",
  adjustNegativeButtonTextColor: "#ffffff",
  treatmentBeforeBackgroundColor: "#ffffff",
  treatmentBeforeBorderColor: "#d4dce3",
  treatmentBeforeTextColor: "#6a7d8e",
  treatmentActiveBackgroundColor: "#dd2c2c",
  treatmentActiveBorderColor: "#c12727",
  treatmentActiveTextColor: "#ffffff",
  treatmentDoneBackgroundColor: "#1696dd",
  treatmentDoneBorderColor: "#127eb7",
  treatmentDoneTextColor: "#ffffff",
  untreatedZoneBackgroundColor: "#ffffff",
  untreatedZoneBorderColor: "#d4dce3",
  untreatedChipBackgroundColor: "#ffffff",
  untreatedChipBorderColor: "#d4dce3",
  untreatedChipTextColor: "#6a7d8e",
}

export const TREATMENT_ROOM_THEME_PRESETS: TreatmentRoomThemePreset[] = [
  {
    id: "sky",
    name: "스카이",
    description: "현재 치료실과 가장 가까운 기본 톤",
    colors: {
      pageBackgroundColor: "#e7f0f8",
      headerBackgroundColor: "#f6fbff",
      cardBackgroundColor: "#f8fbfe",
      emptyCardBackgroundColor: "#eef5fb",
      cardHeaderBackgroundColor: "#edf5fb",
      cardBorderColor: "#cfe0ec",
      cardAccentColor: "#1e9bdc",
      emptyCardAccentColor: "#95b8d3",
    },
  },
  {
    id: "mint",
    name: "민트",
    description: "부드러운 청록 배경",
    colors: {
      pageBackgroundColor: "#e8f7f3",
      headerBackgroundColor: "#f6fffc",
      cardBackgroundColor: "#f7fffc",
      emptyCardBackgroundColor: "#edf8f4",
      cardHeaderBackgroundColor: "#edf8f5",
      cardBorderColor: "#c7e6dd",
      cardAccentColor: "#1fa79b",
      emptyCardAccentColor: "#8fc9c0",
    },
  },
  {
    id: "sand",
    name: "샌드",
    description: "밝은 베이지 톤",
    colors: {
      pageBackgroundColor: "#f7f1e6",
      headerBackgroundColor: "#fffaf2",
      cardBackgroundColor: "#fffdfa",
      emptyCardBackgroundColor: "#faf3e7",
      cardHeaderBackgroundColor: "#f9f1e5",
      cardBorderColor: "#e6d4bc",
      cardAccentColor: "#ca8d35",
      emptyCardAccentColor: "#d3b58d",
    },
  },
  {
    id: "slate",
    name: "슬레이트",
    description: "차분한 회청색 톤",
    colors: {
      pageBackgroundColor: "#e7edf4",
      headerBackgroundColor: "#f4f7fb",
      cardBackgroundColor: "#f8fbfd",
      emptyCardBackgroundColor: "#edf2f7",
      cardHeaderBackgroundColor: "#edf2f7",
      cardBorderColor: "#cfd8e3",
      cardAccentColor: "#5c85b1",
      emptyCardAccentColor: "#96aac1",
    },
  },
]

const TREATMENT_ROOM_THEME_COLOR_PATTERN = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/

const normalizeThemeColor = (value: unknown, fallback: string): string => {
  if (typeof value !== "string") return fallback
  const trimmed = value.trim()
  return TREATMENT_ROOM_THEME_COLOR_PATTERN.test(trimmed) ? trimmed : fallback
}

export const normalizeTreatmentRoomTheme = (theme?: Partial<TreatmentRoomThemeSettings> | null): TreatmentRoomThemeSettings => {
  const source = theme || {}

  return {
    backgroundPresetId:
      typeof source.backgroundPresetId === "string" && TREATMENT_ROOM_THEME_PRESETS.some(preset => preset.id === source.backgroundPresetId)
        ? source.backgroundPresetId
        : DEFAULT_TREATMENT_ROOM_THEME.backgroundPresetId,
    pageBackgroundColor: normalizeThemeColor(source.pageBackgroundColor, DEFAULT_TREATMENT_ROOM_THEME.pageBackgroundColor),
    headerBackgroundColor: normalizeThemeColor(source.headerBackgroundColor, DEFAULT_TREATMENT_ROOM_THEME.headerBackgroundColor),
    headerTitleColor: normalizeThemeColor(source.headerTitleColor, DEFAULT_TREATMENT_ROOM_THEME.headerTitleColor),
    headerEyebrowColor: normalizeThemeColor(source.headerEyebrowColor, DEFAULT_TREATMENT_ROOM_THEME.headerEyebrowColor),
    headerDescriptionColor: normalizeThemeColor(source.headerDescriptionColor, DEFAULT_TREATMENT_ROOM_THEME.headerDescriptionColor),
    iconButtonBackgroundColor: normalizeThemeColor(source.iconButtonBackgroundColor, DEFAULT_TREATMENT_ROOM_THEME.iconButtonBackgroundColor),
    iconButtonBorderColor: normalizeThemeColor(source.iconButtonBorderColor, DEFAULT_TREATMENT_ROOM_THEME.iconButtonBorderColor),
    iconButtonTextColor: normalizeThemeColor(source.iconButtonTextColor, DEFAULT_TREATMENT_ROOM_THEME.iconButtonTextColor),
    cardBackgroundColor: normalizeThemeColor(source.cardBackgroundColor, DEFAULT_TREATMENT_ROOM_THEME.cardBackgroundColor),
    emptyCardBackgroundColor: normalizeThemeColor(source.emptyCardBackgroundColor, DEFAULT_TREATMENT_ROOM_THEME.emptyCardBackgroundColor),
    cardBorderColor: normalizeThemeColor(source.cardBorderColor, DEFAULT_TREATMENT_ROOM_THEME.cardBorderColor),
    cardHeaderBackgroundColor: normalizeThemeColor(source.cardHeaderBackgroundColor, DEFAULT_TREATMENT_ROOM_THEME.cardHeaderBackgroundColor),
    cardHeaderTextColor: normalizeThemeColor(source.cardHeaderTextColor, DEFAULT_TREATMENT_ROOM_THEME.cardHeaderTextColor),
    cardAccentColor: normalizeThemeColor(source.cardAccentColor, DEFAULT_TREATMENT_ROOM_THEME.cardAccentColor),
    emptyCardAccentColor: normalizeThemeColor(source.emptyCardAccentColor, DEFAULT_TREATMENT_ROOM_THEME.emptyCardAccentColor),
    primaryTextColor: normalizeThemeColor(source.primaryTextColor, DEFAULT_TREATMENT_ROOM_THEME.primaryTextColor),
    secondaryTextColor: normalizeThemeColor(source.secondaryTextColor, DEFAULT_TREATMENT_ROOM_THEME.secondaryTextColor),
    accentTextColor: normalizeThemeColor(source.accentTextColor, DEFAULT_TREATMENT_ROOM_THEME.accentTextColor),
    doctorBadgeBackgroundColor: normalizeThemeColor(source.doctorBadgeBackgroundColor, DEFAULT_TREATMENT_ROOM_THEME.doctorBadgeBackgroundColor),
    doctorBadgeTextColor: normalizeThemeColor(source.doctorBadgeTextColor, DEFAULT_TREATMENT_ROOM_THEME.doctorBadgeTextColor),
    patientBadgeBackgroundColor: normalizeThemeColor(source.patientBadgeBackgroundColor, DEFAULT_TREATMENT_ROOM_THEME.patientBadgeBackgroundColor),
    patientBadgeTextColor: normalizeThemeColor(source.patientBadgeTextColor, DEFAULT_TREATMENT_ROOM_THEME.patientBadgeTextColor),
    endButtonBackgroundColor: normalizeThemeColor(source.endButtonBackgroundColor, DEFAULT_TREATMENT_ROOM_THEME.endButtonBackgroundColor),
    endButtonTextColor: normalizeThemeColor(source.endButtonTextColor, DEFAULT_TREATMENT_ROOM_THEME.endButtonTextColor),
    adjustPositiveButtonBackgroundColor: normalizeThemeColor(
      source.adjustPositiveButtonBackgroundColor,
      DEFAULT_TREATMENT_ROOM_THEME.adjustPositiveButtonBackgroundColor
    ),
    adjustPositiveButtonTextColor: normalizeThemeColor(
      source.adjustPositiveButtonTextColor,
      DEFAULT_TREATMENT_ROOM_THEME.adjustPositiveButtonTextColor
    ),
    adjustNegativeButtonBackgroundColor: normalizeThemeColor(
      source.adjustNegativeButtonBackgroundColor,
      DEFAULT_TREATMENT_ROOM_THEME.adjustNegativeButtonBackgroundColor
    ),
    adjustNegativeButtonTextColor: normalizeThemeColor(
      source.adjustNegativeButtonTextColor,
      DEFAULT_TREATMENT_ROOM_THEME.adjustNegativeButtonTextColor
    ),
    treatmentBeforeBackgroundColor: normalizeThemeColor(
      source.treatmentBeforeBackgroundColor,
      DEFAULT_TREATMENT_ROOM_THEME.treatmentBeforeBackgroundColor
    ),
    treatmentBeforeBorderColor: normalizeThemeColor(source.treatmentBeforeBorderColor, DEFAULT_TREATMENT_ROOM_THEME.treatmentBeforeBorderColor),
    treatmentBeforeTextColor: normalizeThemeColor(source.treatmentBeforeTextColor, DEFAULT_TREATMENT_ROOM_THEME.treatmentBeforeTextColor),
    treatmentActiveBackgroundColor: normalizeThemeColor(
      source.treatmentActiveBackgroundColor,
      DEFAULT_TREATMENT_ROOM_THEME.treatmentActiveBackgroundColor
    ),
    treatmentActiveBorderColor: normalizeThemeColor(source.treatmentActiveBorderColor, DEFAULT_TREATMENT_ROOM_THEME.treatmentActiveBorderColor),
    treatmentActiveTextColor: normalizeThemeColor(source.treatmentActiveTextColor, DEFAULT_TREATMENT_ROOM_THEME.treatmentActiveTextColor),
    treatmentDoneBackgroundColor: normalizeThemeColor(source.treatmentDoneBackgroundColor, DEFAULT_TREATMENT_ROOM_THEME.treatmentDoneBackgroundColor),
    treatmentDoneBorderColor: normalizeThemeColor(source.treatmentDoneBorderColor, DEFAULT_TREATMENT_ROOM_THEME.treatmentDoneBorderColor),
    treatmentDoneTextColor: normalizeThemeColor(source.treatmentDoneTextColor, DEFAULT_TREATMENT_ROOM_THEME.treatmentDoneTextColor),
    untreatedZoneBackgroundColor: normalizeThemeColor(source.untreatedZoneBackgroundColor, DEFAULT_TREATMENT_ROOM_THEME.untreatedZoneBackgroundColor),
    untreatedZoneBorderColor: normalizeThemeColor(source.untreatedZoneBorderColor, DEFAULT_TREATMENT_ROOM_THEME.untreatedZoneBorderColor),
    untreatedChipBackgroundColor: normalizeThemeColor(source.untreatedChipBackgroundColor, DEFAULT_TREATMENT_ROOM_THEME.untreatedChipBackgroundColor),
    untreatedChipBorderColor: normalizeThemeColor(source.untreatedChipBorderColor, DEFAULT_TREATMENT_ROOM_THEME.untreatedChipBorderColor),
    untreatedChipTextColor: normalizeThemeColor(source.untreatedChipTextColor, DEFAULT_TREATMENT_ROOM_THEME.untreatedChipTextColor),
  }
}

export const applyTreatmentRoomThemePreset = (theme: TreatmentRoomThemeSettings, presetId: string): TreatmentRoomThemeSettings => {
  const preset = TREATMENT_ROOM_THEME_PRESETS.find(item => item.id === presetId)
  if (!preset) return theme

  return normalizeTreatmentRoomTheme({
    ...theme,
    ...preset.colors,
    backgroundPresetId: preset.id,
  })
}

export const isHiddenTreatmentStatus = (status?: Pick<TreatmentItemStatus, "name"> | null): boolean => {
  return Boolean(status?.name && status.name.startsWith(HIDDEN_TREATMENT_STATUS_NAME_PREFIX))
}

export const normalizeTreatmentStatusName = (name?: string | null): string | undefined => {
  if (!name) return undefined
  return name.startsWith(HIDDEN_TREATMENT_STATUS_NAME_PREFIX) ? name.slice(HIDDEN_TREATMENT_STATUS_NAME_PREFIX.length) : name
}

export const toHiddenTreatmentStatusName = (name?: string | null): string => {
  return `${HIDDEN_TREATMENT_STATUS_NAME_PREFIX}${normalizeTreatmentStatusName(name) || ""}`
}

/** 기본 치료 항목 목록 */
export const DEFAULT_TREATMENT_ITEMS: TreatmentItemDef[] = [
  { id: "hotpack", name: "핫팩", shortName: "핫", order: 0, enabled: true, timerMinutes: DEFAULT_TREATMENT_TIMER_MINUTES },
  { id: "ict", name: "ICT", shortName: "I", order: 1, enabled: true, timerMinutes: DEFAULT_TREATMENT_TIMER_MINUTES },
  { id: "cupping", name: "부항", shortName: "부", order: 2, enabled: true, timerMinutes: DEFAULT_TREATMENT_TIMER_MINUTES },
  { id: "acupuncture", name: "침", shortName: "침", order: 3, enabled: true, timerMinutes: DEFAULT_TREATMENT_TIMER_MINUTES },
  { id: "hydrotherapy", name: "물치", shortName: "물", order: 4, enabled: true, timerMinutes: DEFAULT_TREATMENT_TIMER_MINUTES },
  { id: "moxa", name: "뜸", shortName: "뜸", order: 5, enabled: true, timerMinutes: DEFAULT_TREATMENT_TIMER_MINUTES },
]

